import React, { useState, useEffect } from 'react';
import { User } from '../types';

interface CallHistory {
  callId: string;
  otherUserId: string;
  otherUserCode?: string;
  otherUsername?: string;
  otherUserProfileImage?: string;
  callDate: string; // yyyymmddhhmm形式
  callDuration: number; // 秒
  extensionCount: number; // 延長回数
}

interface CallHistoryPanelProps {
  currentUser: User;
  onUserClick: (user: User) => void;
  onFavoriteToggle?: (userId: string) => void;
  onCallRequest?: (targetUserId: string) => void;
  isFavorite?: (userId: string) => boolean;
  canCallRequest?: (targetUserId: string) => boolean;
}

const CallHistoryPanel: React.FC<CallHistoryPanelProps> = ({
  currentUser,
  onUserClick,
  onFavoriteToggle,
  onCallRequest,
  isFavorite,
  canCallRequest
}) => {
  const [callHistory, setCallHistory] = useState<CallHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<Record<string, User>>({});
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const API_URL = 'https://voicechatapi.kasaneko.com';

  useEffect(() => {
    loadCallHistory();
  }, [currentUser.userId]);

  // ユーザー詳細情報を取得
  useEffect(() => {
    const loadUserDetails = async () => {
      const userIds = callHistory.map(h => h.otherUserId);
      const uniqueUserIds = Array.from(new Set(userIds));
      
      for (const userId of uniqueUserIds) {
        if (!userDetails[userId]) {
          try {
            const response = await fetch(`${API_URL}/api/users/${userId}`);
            const data = await response.json();
            
            if (data.success && data.user) {
              setUserDetails(prev => ({
                ...prev,
                [userId]: {
                  userId: data.user.userId,
                  userCode: data.user.userCode,
                  username: data.user.username,
                  profileImage: data.user.profileImage,
                  gender: data.user.gender,
                  hobbies: data.user.hobbies,
                  bio: data.user.bio,
                  position: data.user.position || { x: 0, y: 0, z: 0 },
                  isOnline: data.user.isOnline,
                  avatar: data.user.avatar
                }
              }));
            }
          } catch (error) {
            console.error(`ユーザー情報取得エラー (${userId}):`, error);
          }
        }
      }
    };

    if (callHistory.length > 0) {
      loadUserDetails();
    }
  }, [callHistory]);

  const loadCallHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/users/call-history/${currentUser.userId}`);
      const data = await response.json();

      if (data.success && data.callHistory) {
        setCallHistory(data.callHistory);
      } else {
        setCallHistory([]);
      }
      
    } catch (error) {
      console.error('通話履歴取得エラー:', error);
      setError('通話履歴の取得に失敗しました');
      setCallHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCallDate = (dateStr: string) => {
    // yyyymmddhhmm形式を変換
    if (dateStr.length !== 12) {
      return dateStr;
    }
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const hour = dateStr.substring(8, 10);
    const minute = dateStr.substring(10, 12);
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}分${secs}秒`;
    }
    return `${secs}秒`;
  };

  if (loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#333',
        fontSize: '18px'
      }}>
        読み込み中...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#c62828',
        fontSize: '16px',
        padding: '20px',
        textAlign: 'center'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      paddingBottom: 'calc(100px + max(env(safe-area-inset-bottom, 0px), 50px))',
      overflow: 'auto',
      background: '#ffffff'
    }}>
      <h2 style={{
        color: '#667eea',
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        通話履歴
      </h2>

      {selectedUser ? (
        <div style={{
          padding: '20px',
          height: '100%',
          overflow: 'auto'
        }}>
          <button
            onClick={() => setSelectedUser(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              marginBottom: '15px',
              color: '#333'
            }}
          >
            ← 戻る
          </button>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '15px',
            padding: '25px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}>
            {/* お気に入りボタン（右上） */}
            {onFavoriteToggle && isFavorite && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFavoriteToggle(selectedUser.userId);
                }}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '32px',
                  padding: '5px',
                  lineHeight: '1',
                  zIndex: 10
                }}
                title={isFavorite(selectedUser.userId) ? 'お気に入り解除' : 'お気に入り追加'}
              >
                {isFavorite(selectedUser.userId) ? '❤️' : '🤍'}
              </button>
            )}

            {/* プロフィール画像 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: selectedUser.profileImage
                  ? `url(${selectedUser.profileImage}) center/cover`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '48px',
                border: '4px solid #667eea',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                {!selectedUser.profileImage && '👤'}
              </div>
            </div>

            {/* ユーザー名 */}
            <h3 style={{
              textAlign: 'center',
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#333',
              marginBottom: '10px'
            }}>
              {selectedUser.username}
            </h3>

            {/* ユーザーID */}
            {selectedUser.userCode && (
              <div style={{
                textAlign: 'center',
                fontSize: '14px',
                color: '#666',
                marginBottom: '20px'
              }}>
                ID: {selectedUser.userCode}
              </div>
            )}

            {/* 性別 */}
            {selectedUser.gender && (
              <div style={{
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '5px'
                }}>
                  性別
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#333'
                }}>
                  {selectedUser.gender}
                </div>
              </div>
            )}

            {/* 趣味 */}
            {selectedUser.hobbies && (
              <div style={{
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '5px'
                }}>
                  趣味
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {selectedUser.hobbies}
                </div>
              </div>
            )}

            {/* 自己紹介 */}
            {selectedUser.bio && (
              <div style={{
                marginBottom: '20px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '5px'
                }}>
                  自己紹介
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: '1.6'
                }}>
                  {selectedUser.bio}
                </div>
              </div>
            )}

            {/* 通話依頼ボタン（プロフィール内の下） */}
            {onCallRequest && canCallRequest && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (canCallRequest(selectedUser.userId)) {
                    onCallRequest(selectedUser.userId);
                  }
                }}
                disabled={!canCallRequest(selectedUser.userId)}
                style={{
                  width: '100%',
                  padding: '15px',
                  background: canCallRequest(selectedUser.userId) ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: canCallRequest(selectedUser.userId) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '25px'
                }}
              >
                <span>⭐</span>
                {canCallRequest(selectedUser.userId) ? '通話依頼' : '通話依頼（利用不可）'}
              </button>
            )}
          </div>
        </div>
      ) : callHistory.length === 0 ? (
        <div style={{
          textAlign: 'center',
          color: '#999',
          fontSize: '16px',
          marginTop: '40px'
        }}>
          通話履歴はありません
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}>
          {callHistory.slice(0, 10).map((history) => {
            const user = userDetails[history.otherUserId];
            const userAvailable = user !== undefined;

            return (
              <div
                key={history.callId}
                onClick={() => {
                  if (userAvailable && user) {
                    setSelectedUser(user);
                  }
                }}
                style={{
                  background: userAvailable ? 'rgba(102, 126, 234, 0.05)' : '#ffebee',
                  border: `1px solid ${userAvailable ? 'rgba(102, 126, 234, 0.2)' : '#ef5350'}`,
                  borderRadius: '12px',
                  padding: '15px',
                  cursor: userAvailable ? 'pointer' : 'default',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: userAvailable ? 'transform 0.2s' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}
                onMouseEnter={(e) => {
                  if (userAvailable) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (userAvailable) {
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {/* アイコン */}
                {userAvailable && user ? (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: user.profileImage
                      ? `url(${user.profileImage}) center/cover`
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0,
                    border: '2px solid #667eea'
                  }}>
                    {!user.profileImage && '👤'}
                  </div>
                ) : (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: '#ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>
                    ?
                  </div>
                )}

                {/* ユーザー情報 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {userAvailable && user ? (
                    <>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#333',
                        marginBottom: '5px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.username}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px'
                      }}>
                        ユーザーID: {user.userId}
                      </div>
                      {user.userCode && (
                        <div style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '8px'
                        }}>
                          ユーザーコード: {user.userCode}
                        </div>
                      )}
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        marginBottom: '4px'
                      }}>
                        通話開始日時: {history.callDate}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        marginBottom: '4px'
                      }}>
                        延長回数: {history.extensionCount}回
                      </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#667eea',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                        }}>
                        総通話時間: {formatDuration(history.callDuration)}
                        </div>
                    </>
                  ) : (
                    <>
                    <div style={{
                      color: '#c62828',
                      fontSize: '14px',
                        fontWeight: 'bold',
                        marginBottom: '8px'
                    }}>
                        ユーザー情報が取得できませんでした。
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px'
                      }}>
                        ユーザーID: {history.otherUserId}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        marginBottom: '4px'
                      }}>
                        通話開始日時: {history.callDate}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        marginBottom: '4px'
                      }}>
                        延長回数: {history.extensionCount}回
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#667eea',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        総通話時間: {formatDuration(history.callDuration)}
                    </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CallHistoryPanel;

